
// src/components/EnhancedErrorBoundary.jsx

import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';

/**
 * ============================================================
 * WEB NAVIGATION HELPERS
 * ============================================================
 *
 * React Native used:
 *   createNavigationContainerRef()
 *   navigationRef.getCurrentRoute()
 *
 * ReactJS Web uses:
 *   react-router-dom
 *   useLocation()
 *   useNavigate()
 *   useParams()
 *
 * Because React Router hooks can only be used inside a Router,
 * route information is provided through a small global tracker.
 */

// ------------------------------------------------------------
// Current route state
// ------------------------------------------------------------

let currentRoute = {
  pathname: '/',
  search: '',
  hash: '',
  params: {},
};

/**
 * Get the current route pathname.
 *
 * Example:
 *   /admin/dashboard
 */
export function getCurrentRoute() {
  return currentRoute.pathname || null;
}

/**
 * Get the current route parameters.
 *
 * Example:
 *   /provider/:providerId
 *
 * Returns:
 *   { providerId: '123' }
 */
export function getCurrentRouteParams() {
  return currentRoute.params || {};
}

/**
 * Get the complete current location.
 */
export function getCurrentLocation() {
  return {
    pathname: currentRoute.pathname,
    search: currentRoute.search,
    hash: currentRoute.hash,
    params: currentRoute.params,
  };
}

/**
 * Internal component used to keep the route tracker
 * synchronized with React Router.
 */
const RouteTracker = () => {
  const location = useLocation();
  const params = useParams();

  React.useEffect(() => {
    currentRoute = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      params: { ...params },
    };
  }, [
    location.pathname,
    location.search,
    location.hash,
    params,
  ]);

  return null;
};

/**
 * ============================================================
 * NAVIGATION TRACKER
 * ============================================================
 *
 * Add <NavigationTracker /> once inside BrowserRouter.
 *
 * Example:
 *
 * <BrowserRouter>
 *   <NavigationTracker />
 *   <App />
 * </BrowserRouter>
 */
export const NavigationTracker = () => {
  return <RouteTracker />;
};

/**
 * ============================================================
 * ERROR BOUNDARY HOC
 * ============================================================
 *
 * React Native:
 *
 * <ErrorBoundary
 *   screenName={screenName}
 *   navigation={navigationRef}
 * >
 *
 * React Web:
 *
 * We provide:
 *   navigate
 *   location
 *   params
 *
 * to the ErrorBoundary.
 */

// ------------------------------------------------------------
// Basic error boundary wrapper
// ------------------------------------------------------------

export const withErrorBoundary = (
  ScreenComponent,
  screenName
) => {
  const WrappedComponent = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    return (
      <ErrorBoundary
        screenName={screenName}
        navigation={navigate}
        location={location}
        params={params}
        {...props}
      >
        <ScreenComponent {...props} />
      </ErrorBoundary>
    );
  };

  WrappedComponent.displayName = `withErrorBoundary(${
    ScreenComponent.displayName ||
    ScreenComponent.name ||
    'Component'
  })`;

  return WrappedComponent;
};

// ------------------------------------------------------------
// Screen error boundary wrapper
// ------------------------------------------------------------

export const withScreenErrorBoundary = (
  ScreenComponent,
  screenName
) => {
  const WrappedComponent = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    return (
      <ErrorBoundary
        screenName={screenName}
        componentName={screenName}
        navigation={navigate}
        location={location}
        params={params}
        {...props}
      >
        <ScreenComponent {...props} />
      </ErrorBoundary>
    );
  };

  WrappedComponent.displayName = `withScreenErrorBoundary(${
    ScreenComponent.displayName ||
    ScreenComponent.name ||
    'Component'
  })`;

  return WrappedComponent;
};

/**
 * ============================================================
 * MAIN APP ERROR BOUNDARY
 * ============================================================
 */

export const AppErrorBoundary = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  return (
    <ErrorBoundary
      screenName="AppRoot"
      componentName="AppRoot"
      navigation={navigate}
      location={location}
      params={params}
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * ============================================================
 * TAB SCREEN WRAPPER
 * ============================================================
 *
 * React Native returned:
 *
 * {
 *   component: WrappedComponent,
 *   options
 * }
 *
 * React Router does not need createTabScreen().
 *
 * This function is retained for compatibility with existing
 * code so you don't have to immediately rewrite every import.
 */

export const createTabScreen = (
  Component,
  screenName,
  options = {}
) => {
  const WrappedComponent = withScreenErrorBoundary(
    Component,
    screenName
  );

  return {
    Component: WrappedComponent,
    component: WrappedComponent,
    options,
  };
};

/**
 * ============================================================
 * STACK SCREEN WRAPPER
 * ============================================================
 *
 * React Router doesn't have "stack screens", but this helper
 * is retained so existing application code can continue using
 * the same structure.
 */

export const createStackScreen = (
  Component,
  screenName,
  options = {}
) => {
  const WrappedComponent = withScreenErrorBoundary(
    Component,
    screenName
  );

  return {
    Component: WrappedComponent,
    component: WrappedComponent,
    options,
  };
};

/**
 * ============================================================
 * DEFAULT EXPORT
 * ============================================================
 */

export default {
  getCurrentRoute,
  getCurrentRouteParams,
  getCurrentLocation,
  withErrorBoundary,
  withScreenErrorBoundary,
  AppErrorBoundary,
  NavigationTracker,
  createTabScreen,
  createStackScreen,
};

